import {useState} from 'react';
import styled from 'styled-components';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import {LabelledButton} from './LabelledButton';
import {EmptyState} from './EmptyState';
import {graphql, useFragment} from 'react-relay';
import type {LinkedTasksTab_ticket$key} from './__generated__/LinkedTasksTab_ticket.graphql';
import {useUnlinkForecastTaskFromTicket} from '../hooks/useUnlinkForecastTaskFromTicket';
import {useAuth} from '../../auth/context/AuthContext';
import {hasTicketPerm, TICKET_PERMS} from '../../auth/utils/ticketPerms';
import {LogTimeModal} from './LogTimeModal';
import {useOpenNativeTaskModal} from '../hooks/useOpenNativeTaskModal';

const LinkedTasksTabFragment = graphql`
	fragment LinkedTasksTab_ticket on Ticket {
		id
		aiSuggestedLinkedTaskIds
		totalLinkedTaskMinutes
		linkedTasks {
			id
			companyTaskId
			name
			totalMinutesRegistered
			project {
				id
				name
			}
			assignedPersons {
				id
				fullName
			}
		}
	}
`;

interface Props {
	ticket: LinkedTasksTab_ticket$key;
	onAddTask?: () => void;
	currentPersonId: string;
	/** Called after time is logged so the ticket-level minute rollup refreshes. */
	onRefetch?: () => void;
}

/** Format a minute count as "Xh Ym" (or "Ym" / "0m"). */
function formatMinutes(minutes: number): string {
	if (!minutes) return '0m';
	const h = Math.floor(minutes / 60);
	const m = minutes % 60;
	if (h === 0) return `${m}m`;
	if (m === 0) return `${h}h`;
	return `${h}h ${m}m`;
}

const Stack = styled.div`
	> * + * {
		margin-top: 16px;
	}
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const Rollup = styled.p`
	margin: 0;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #64748b;
`;

const EmptyCard = EmptyState;

const EmptyTitle = styled.p`
	margin: 0;
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: 500;
	color: #0f172a;
`;

const TaskCard = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	border-radius: 8px;
	border: 1px solid ${p => p.theme.sys.color.border.decorative};
	background: #ffffff;
	padding: 12px 16px;
	box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
`;

const TaskMain = styled.div`
	min-width: 0;
`;

const TaskName = styled.p`
	margin: 0;
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: 500;
	color: #0f172a;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const TaskMeta = styled.p`
	margin: 2px 0 0;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #64748b;
`;

const AiChip = styled.span`
	margin-left: 8px;
	padding: 1px 6px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	background: #e9d5ff;
	color: ${p => p.theme.sys.color.brand.primary['1']};
	border-radius: 9999px;
`;

const TaskTime = styled.span`
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	color: #475569;
	white-space: nowrap;
`;

const RemoveIcon = styled.svg`
	height: 16px;
	width: 16px;
`;

export function LinkedTasksTab({ticket: ticketRef, onAddTask, currentPersonId, onRefetch}: Props) {
	const data = useFragment(LinkedTasksTabFragment, ticketRef);
	const openTaskDetail = useOpenNativeTaskModal();
	const {user} = useAuth();
	const {submit: unlinkTask, loading: unlinking} = useUnlinkForecastTaskFromTicket();
	const canManage = !!user && hasTicketPerm(user.ticketPerms, TICKET_PERMS.MANAGE);
	const [logTask, setLogTask] = useState<{id: string; name: string; projectId: string | null} | null>(null);

	const tasks = data.linkedTasks;
	const aiSuggestedIds = new Set(data.aiSuggestedLinkedTaskIds);

	const handleUnlink = (forecastTaskId: string) => {
		unlinkTask({ticketId: data.id, forecastTaskId}).catch(() => {});
	};

	return (
		<Stack>
			<Header>
				<Rollup>
					{tasks.length} {tasks.length === 1 ? 'task' : 'tasks'} · {formatMinutes(data.totalLinkedTaskMinutes)} logged
					across linked tasks
				</Rollup>
				{onAddTask && (
					<Button emphasis={EMPHASIS.HIGH} onPress={() => onAddTask?.()}>
						+ Add task
					</Button>
				)}
			</Header>

			{tasks.length === 0 ? (
				<EmptyCard>
					<EmptyTitle>No tasks linked to this ticket yet.</EmptyTitle>
				</EmptyCard>
			) : (
				tasks.map(task => {
					const assignees = task.assignedPersons
						?.map(p => p?.fullName)
						.filter((n): n is string => !!n)
						.join(', ');
					const taskName = task.name ?? 'Untitled task';
					return (
						<TaskCard key={task.id}>
							<TaskMain>
								{task.companyTaskId != null ? (
									<LabelledButton
										emphasis={EMPHASIS.LOW}
										label={`Open task detail for ${taskName}`}
										onPress={() => openTaskDetail(task.companyTaskId as number)}
									>
										{taskName}
										{aiSuggestedIds.has(task.id) && <AiChip>Created from AI suggestion</AiChip>}
									</LabelledButton>
								) : (
									<TaskName>
										{taskName}
										{aiSuggestedIds.has(task.id) && <AiChip>Created from AI suggestion</AiChip>}
									</TaskName>
								)}
								<TaskMeta>
									{task.project?.name ?? 'No project'}
									{assignees ? ` · ${assignees}` : ''}
								</TaskMeta>
							</TaskMain>
							<TaskTime>{formatMinutes(task.totalMinutesRegistered ?? 0)}</TaskTime>
							<LabelledButton
								emphasis={EMPHASIS.LOW}
								label={`Log time for ${taskName}`}
								onPress={() => setLogTask({id: task.id, name: taskName, projectId: task.project?.id ?? null})}
							>
								Log time
							</LabelledButton>
							{canManage && (
								<LabelledButton
									emphasis={EMPHASIS.LOW}
									label={`Unlink ${task.name ?? 'task'}`}
									disabled={unlinking}
									onPress={() => handleUnlink(task.id)}
								>
									<RemoveIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
										<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
									</RemoveIcon>
								</LabelledButton>
							)}
						</TaskCard>
					);
				})
			)}

			{logTask && (
				<LogTimeModal
					open
					taskId={logTask.id}
					projectId={logTask.projectId}
					taskName={logTask.name}
					personId={currentPersonId}
					ticketId={data.id}
					onClose={() => setLogTask(null)}
					onSuccess={() => {
						setLogTask(null);
						onRefetch?.();
					}}
				/>
			)}
		</Stack>
	);
}
